import { RefObject, useEffect, useState } from "react";
import * as THREE from "three";
import { RtfmAppState } from "../hooks/rtfmAppStateShared";
import { RtfmDerivedState } from "../hooks/useRtfmLifecycle.js";
import { useTheme } from "../hooks/useTheme.js";
import { twMerge } from "tailwind-merge";
import { MouseLeftClickIcon } from "@phosphor-icons/react";
import { SceneCanvas } from "./SceneCanvas";
import { isMobileDevice } from "../utils/device-detection.js";

interface StreamShellProps {
    state: RtfmAppState;
    derived: RtfmDerivedState;
    viewportRef: RefObject<HTMLDivElement | null>;
    controlsRef: RefObject<HTMLDivElement | null>;
    onCanvasReady?: (context: {
        renderer: THREE.WebGLRenderer;
        scene: THREE.Scene;
        camera: THREE.Camera;
    }) => void;
    className?: string;
}

export const StreamShell = ({
    state,
    viewportRef,
    onCanvasReady,
    className,
}: StreamShellProps) => {

    const { theme } = useTheme();

    return (
        <div
            id="viewport-container"
            ref={viewportRef}
            className={twMerge(
                "relative isolate flex flex-col items-center justify-center bg-transparent overflow-hidden pointer-events-auto select-none",
                "w-full h-full max-w-[512px] max-h-[512px]", //md:w-[512px] md:h-[512px]
                `border-none ${state.cursorLocked ? "border-transparent" : "border-white"}`,
                className
            )}
        >
            {false && <img
                id="viewport-bg"
                alt="stream glow backdrop"
                className={`absolute inset-0 -z-10 h-full w-full object-cover blur-[12em] 
                        ${theme === "dark" ? "opacity-50" : "opacity-75"} 
                        ${state.cursorLocked ? "scale-150" : "scale-150"} duration-500 pointer-events-none
                        `}
            />}

            <BadConnectionBadge visible={state.badConnection} />

            <div className="absolute inset-0 rounded-[1em] overflow-hidden">
                <SceneCanvas onReady={onCanvasReady} />
            </div>

            {/* <div className={`hidden sm:block absolute -bottom-10 max-w-sm min-w-sm w-full z-50 pointer-events-none duration-200 ${state.cursorLocked ? "opacity-100 translate-y-0" : "opacity-0 mt-12 -translate-y-0.5"}`}>
                    <ControlDock controlsRef={controlsRef} visible={derived.isActive} />
                </div> */}
        </div>
    );
};


interface StreamInfoHeaderProps {
    state: RtfmAppState;
}

export const StreamInfoFooter = ({
    state,
}: StreamInfoHeaderProps) => {
    return (
        <div className="w-full h-auto shrink-0 flex flex-col gap-2">
            {state.sceneLoadError !== null && (
                <div className="flex justify-center text-red-500 border border-red-500/25 p-0.5 text-[0.9em] text-primary/70">
                    {state.sceneLoadError}
                </div>
            )}
        </div>
    );
};


interface StreamInfoDockProps {
    state: RtfmAppState;
    statsContainerRef: RefObject<HTMLDivElement | null>;
    statsFpsRef: RefObject<HTMLDivElement | null>;
    statsResolutionRef: RefObject<HTMLDivElement | null>;
    statsViewportRef: RefObject<HTMLDivElement | null>;
}

export const StreamInfoDock = ({
    state,
    statsContainerRef,
    statsFpsRef,
    statsResolutionRef,
    statsViewportRef,
}: StreamInfoDockProps) => {
    return (
        //TODO: show stats to users?
        <div
            ref={statsContainerRef}
            className="hidden w-full flex-row items-center gap-4 px-3"
            data-react-managed="stats"
        >
            <div className={"text-center"} ref={statsFpsRef}>
                {Math.round(state.fps)} fps
            </div>
            <div className={"text-center"} ref={statsResolutionRef}>
                {state.resolution.width}×{state.resolution.height}
            </div>
            <div ref={statsViewportRef} className="viewport-mode">
                    {state.viewportMode === "fit" ? "Fit" : "Native"}
                </div>
        </div>
    );
};


const TIMER_VARIANT_CLASS: Record<
    RtfmDerivedState["timerVariant"],
    string
> = {
    default: "text-green-600",
    warning: "text-amber-600",
    critical:
        "text-red-400 animate-pulse [animation-duration:1.5s]",
};

const INDICATOR_VARIANT_CLASS: Record<
    RtfmDerivedState["timerVariant"],
    string
> = {
    default: "bg-green-500",
    warning: "bg-amber-500",
    critical: "bg-red-300 animate-pulse [animation-duration:1.5s]",
};

interface StatusBadgeProps {
    containerRef: RefObject<HTMLDivElement | null>;
    indicatorRef: RefObject<HTMLSpanElement | null>;
    textRef: RefObject<HTMLSpanElement | null>;
    status: RtfmAppState["connectionStatus"];
    message: string;
    timerVariant: RtfmDerivedState["timerVariant"];
}

const BadConnectionBadge = ({ visible }: { visible: boolean }) => {
    if (!visible) {
        return null;
    }
    return (
        <div className="absolute top-2 right-2 border px-1 py-0.5 animate-pulse border-amber-400/40 bg-white/70 text-amber-700">
            slow connection
        </div>
    );
};

interface ErrorBannerProps {
    state: Pick<RtfmAppState, "error">;
    containerRef: RefObject<HTMLDivElement | null>;
    labelRef: RefObject<HTMLSpanElement | null>;
}

export const ErrorBanner = ({ state, containerRef, labelRef }: ErrorBannerProps) => {
    const visible = Boolean(state.error);
    return (
        <div
            ref={containerRef}
            data-react-managed="error-bar"
            className={`error-bar border px-4 py-2 transition-opacity duration-300 ${visible ? "opacity-100" : "opacity-0"} border-red-400/50 bg-red-500/10 text-red-400`}
            role="status"
            aria-live="polite"
        >
            <span ref={labelRef}>{state.error}</span>
        </div>
    );
};


export const StatusBadge = ({
    containerRef,
    indicatorRef,
    textRef,
    status,
    message,
    timerVariant,
}: StatusBadgeProps) => {

    const green = status === "connected" && timerVariant === "default";
    const amber = status === "connecting" || status === "reconnecting" || timerVariant === "warning";
    const red = status === "disconnected" || timerVariant === "critical";

    const tone =
        green
            ? TIMER_VARIANT_CLASS['default']
            : amber
                ? TIMER_VARIANT_CLASS['warning']
                : red
                    ? TIMER_VARIANT_CLASS['critical']
                    : TIMER_VARIANT_CLASS['default'];
    const indicatorClass =
        green
            ? INDICATOR_VARIANT_CLASS['default']
            : amber
                ? INDICATOR_VARIANT_CLASS['warning']
                : red
                    ? INDICATOR_VARIANT_CLASS['critical']
                    : INDICATOR_VARIANT_CLASS['default'];

    return (
        <div
            ref={containerRef}
            className={`status flex items-center gap-3 capitalize ${tone}`}
            data-react-managed="status"
        >
            <span
                ref={indicatorRef}
                className={`status-indicator inline-block h-2 w-2 transition-all duration-300 ${indicatorClass}`}
                data-connection={status}
            />
            <span ref={textRef} className="status-text whitespace-nowrap">
                {message}
            </span>
        </div>
    );
};


interface TimerBadgeProps {
    variant: RtfmDerivedState["timerVariant"];
    label: string;
}

export const TimerBadge = ({ variant, label }: TimerBadgeProps) => {
    const variantClass = TIMER_VARIANT_CLASS[variant];
    return (
        <div
            className={`${variantClass}`}
        >
            {label}
        </div>
    );
};
